import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, FormControl, Message, Schema, SelectPicker } from "rsuite";
import styled from "styled-components";

import { BaseInputField, BaseInputNumberField, BaseSelectPicker } from "components/base/BaseForm";
import { SCREEN_MEDIA, currency_names_data } from "const";
import { withTranslation } from "hoc/WithTranslation";
import { Spacer } from "components/base/Spacer";
import { useWindowWidth } from "hooks";
import m from "definedMessages";
import * as S from "../styled";

const { sm } = SCREEN_MEDIA;
const { StringType, NumberType } = Schema.Types;


const ModalRequestPayment = ({
    show,
    onClose,
    maxWidth = 700,
    paymentDetailsItems,
    currencyName,
    currencyList,
    requestPayment,
    pickedCurrencyKey,
    setPickedCurrencyKey,
    billingCurrencyList = [],
    getPaymentRequestList,
    getPaymentDetailsList,
    handleShowModalAddPaymentDetail,
    dropdownVisibility,
    amountLimitMin = 0.01,
    onSuccess,
    formValue,
    setFormValue,

    formatMessage
}) => {
    const resizedWidth = useWindowWidth();
    const [loading, setLoading] = useState(false);
    const formRef = useRef(null);

    const currentBillingCurrency = useMemo(() => {
        if (!billingCurrencyList) {
            return {};
        }

        return billingCurrencyList.find(item => item?.cur_key === pickedCurrencyKey) || {};
    }, [billingCurrencyList, pickedCurrencyKey]);

    const availableForPayment = currentBillingCurrency?.available_for_payment || 0;

    const presentBillingCurrency = useMemo(() => {
        if (!billingCurrencyList) {
            return [];
        }

        return billingCurrencyList
            .filter(Boolean)
            .map(item => item.cur_key);
    }, [billingCurrencyList]);

    const singleCurKey = presentBillingCurrency.length === 1 ? presentBillingCurrency[0] : null;

    const singleCurrency = singleCurKey ? currency_names_data[singleCurKey] : null;

    const filteredPaymentDetailsList = useMemo(() => {
        return paymentDetailsItems.filter(paymentDetail => paymentDetail.cur_key === pickedCurrencyKey);
    }, [paymentDetailsItems, pickedCurrencyKey]);

    const formModel = useMemo(() => Schema.Model({
        amount: NumberType()
            .isRequired(formatMessage(m.thisFieldIsRequired))
            .min(amountLimitMin, formatMessage(m.minNumber, { number: amountLimitMin }))
            .max(availableForPayment, formatMessage(m.maxNumber, { number: availableForPayment })),
        payment_detail_id: StringType()
            .isRequired(formatMessage(m.thisFieldIsRequired)),
    }), [amountLimitMin, availableForPayment, formatMessage]);

    useEffect(() => {
        if (formRef.current?.cleanErrors) {
            formRef.current.cleanErrors();
        }
    }, [amountLimitMin]);

    useEffect(() => {
        if (show && pickedCurrencyKey) {
            getPaymentDetailsList({ cur_key: pickedCurrencyKey });
        }
    }, [getPaymentDetailsList, pickedCurrencyKey, show]);

    const handleSubmit = useCallback(async () => {
        if (!formRef.current || !formRef.current.check()) {
            return null;
        }

        setLoading(true);

        const value = formRef.current.getFormValue();
        const data = {
            cur_key: pickedCurrencyKey,
            ...value,
            amount: +value.amount,
            target: {
                payment_detail_id: value.payment_detail_id,
            },
        };

        delete data.payment_detail_id;

        try {
            const response = await requestPayment(data);

            if (response && !response.error) {
                onSuccess(+value.amount);

                getPaymentRequestList();
                onClose();
            }
        } finally {
            setLoading(false);
        }

        return null;
    }, [getPaymentRequestList, onClose, onSuccess, pickedCurrencyKey, requestPayment]);

    const renderMenuItem = useCallback((label, item) => {
        return item.status === 1
            ? label
            : <span style={{ color: "var(--color-disabled)" }}>{label}</span>;
    }, []);

    return (
        <S.FormModal
            show={show}
            title={formatMessage(m.paymentRequest)}
            width={resizedWidth > maxWidth ? maxWidth : resizedWidth}
            showFooter={true}
            confirmButtonText={formatMessage(m.confirm)}
            loading={loading}
            disabled={!pickedCurrencyKey}
            focusTarget="close"
            onConfirm={handleSubmit}
            onClose={() => {
                onClose(false);
            }}
            onExited={() => {
                setFormValue({ amount: 0 });
            }}
        >
            <Form
                ref={formRef}
                model={formModel}
                formValue={formValue}
                onChange={setFormValue}
            >
                {billingCurrencyList && billingCurrencyList.length
                    ? billingCurrencyList.map(currencyKey => {
                        const available = currencyKey.available_for_payment || 0;
                        const currentCurrencyName = currencyKey?.currency_name;

                        return (
                            <CurrencyText key={currencyKey.cur_key}>
                                {formatMessage(m.readyForPaymentInfo, { count: `${available} ${currentCurrencyName}` })}
                            </CurrencyText>
                        );
                    })
                    : null}

                <Spacer />

                {!pickedCurrencyKey && (
                    <Message
                        type="info"
                        description={formatMessage(m.currencyToCreatePayment)}
                    />
                )}

                <Spacer />

                <S.FormRow>
                    <S.FormItem>
                        <S.FormGroup>
                            <S.Label>{formatMessage(m.amount)}</S.Label>

                            <S.FieldAmountWrapper>
                                <S.Field
                                    accepter={BaseInputNumberField}
                                    name="amount"
                                />
                            </S.FieldAmountWrapper>

                            {currencyName && <S.Currency>{currencyName}</S.Currency>}

                            <StyledCurrencyPicker>
                                {presentBillingCurrency.length > 1 ? (
                                    <StyledFormControl
                                        name="cur_key"
                                        accepter={SelectPicker}
                                        data={currencyList}
                                        labelKey="name"
                                        valueKey="cur_key"
                                        cleanable={false}
                                        searchable={false}
                                        onChange={(value) => {
                                            setPickedCurrencyKey(value);
                                            setFormValue(prev => ({
                                                ...prev,
                                                cur_key: value,
                                                payment_detail_id: null,
                                            }));
                                        }}
                                    />
                                ) : singleCurrency ? (
                                    <SingleCurrencyName>{singleCurrency}</SingleCurrencyName>
                                ) : null}
                            </StyledCurrencyPicker>
                        </S.FormGroup>
                    </S.FormItem>

                    <S.FormItem grow>
                        <S.FormGroup>
                            <S.Label>{formatMessage(m.paymentInfo)}</S.Label>

                            <S.Field
                                accepter={BaseSelectPicker}
                                data={filteredPaymentDetailsList}
                                cleanable={false}
                                name="payment_detail_id"
                                valueKey="id"
                                block={true}
                                placement={resizedWidth <= sm ? "auto" : "bottomStart"}
                                labelKey="payment_detail_info"
                                menuClassName={dropdownVisibility ? "" : "hide-dropdown"}
                                renderMenuItem={renderMenuItem}
                                disabled={!pickedCurrencyKey}
                                renderExtraFooter={() => (
                                    <Button
                                        appearance="link"
                                        onClick={() => {
                                            handleShowModalAddPaymentDetail();
                                        }}
                                    >
                                        + {formatMessage(m.addNew)}
                                    </Button>
                                )}
                            />
                        </S.FormGroup>
                    </S.FormItem>
                </S.FormRow>

                <Spacer />

                <S.Field
                    name="comment"
                    accepter={BaseInputField}
                    componentClass="textarea"
                    placeholder={formatMessage(m.comment)}
                />
            </Form>
        </S.FormModal>
    );
};

export default withTranslation(ModalRequestPayment);

const StyledFormControl = styled(FormControl)`
    && {
        width: 90px;
    }
`;

const SingleCurrencyName = styled.span`
    && {
    }
`;

const StyledCurrencyPicker = styled.div`
    && {
        display: flex;
        align-items: center;
        margin-left: 10px;

        .rs-form-control-wrapper {
            width: 90px;
            margin-right: 20px;
        }

        .rs-message .rs-message-container {
            padding: 10px 20px 10px 20px;
            height: 40px;
            display: flex;
            justify-content: center;
        }
    }
`;

const CurrencyText = styled.p`
    && {
        margin-top: 10px;
    }
`;
